import { Link } from 'react-router-dom'
import { Menu, Search } from 'lucide-react'
import { APP_NAME } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { LogoMark } from '@/components/brand'
import { useUiStore } from '@/stores'
import { IconButton } from '@/components/ui'
import { NotificationBell } from './NotificationBell'
import { ThemeToggle } from './ThemeToggle'
import { UserMenu } from './UserMenu'
import { TenantSwitcher } from './TenantSwitcher'

/**
 * Sticky global header: navigation trigger on mobile, search, tenant switcher,
 * notifications, theme, account.
 *
 * It stays visible while the page scrolls because the notification bell and the
 * tenant switcher are the two things a user reaches for mid-task — and on
 * mobile it is the only route back to navigation.
 */
export function Topbar() {
  const t = useT()
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen)
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen)

  return (
    <header
      className="sticky top-0 z-sticky flex h-topbar shrink-0 items-center gap-2 border-b border-border
        bg-canvas/85 px-3 backdrop-blur supports-[backdrop-filter]:bg-canvas/70 sm:px-4"
    >
      <span className="lg:hidden">
        <IconButton label={t('nav.openNavigation')} icon={<Menu />} onClick={() => setMobileNavOpen(true)} />
      </span>

      <Link to="/" className="flex items-center gap-2 lg:hidden">
        <LogoMark className="h-7 w-7" />
        <span className="sr-only">{t('topbar.home', { app: APP_NAME })}</span>
      </Link>

      {/* Desktop: a wide affordance that opens the palette. Mobile: an icon. */}
      <button
        type="button"
        onClick={() => setCommandPaletteOpen(true)}
        className="ml-1 hidden h-9 max-w-md flex-1 items-center gap-2 rounded-md border border-border
          bg-surface px-3 text-left text-base text-fg-subtle transition-colors hover:border-border-strong md:flex"
      >
        <Search aria-hidden className="h-4 w-4" />
        <span className="flex-1 truncate">{t('topbar.searchWide')}</span>
        <kbd className="rounded border border-border bg-surface-muted px-1.5 py-0.5 text-2xs font-medium">
          ⌘K
        </kbd>
      </button>
      <span className="md:hidden">
        <IconButton label={t('common.search')} icon={<Search />} onClick={() => setCommandPaletteOpen(true)} />
      </span>

      <div className="ml-auto flex items-center gap-1">
        <span className="hidden sm:block">
          <TenantSwitcher />
        </span>
        <NotificationBell />
        <span className="hidden sm:block">
          <ThemeToggle />
        </span>
        <UserMenu />
      </div>
    </header>
  )
}
